import { defineMessages } from "react-intl"
import { ColumnDefinition, ColumnDefinitionCategory, DataType, TotalType } from "."
import { ObjectType, ObjectTypeConfig } from ".."
import Duration from "../../../../common/date/duration.component.jsx"
import DisplayReferences from "../../../../scheduling-page/displayreferences.component.jsx"


import { PREFERENCE_TYPE } from "../../constants"

export enum AppointmentsColumnIds {
    IDENTIFIER = "identifier",
    CONTACT = "contact",
    TITLE = "title",
    START_AT_DATE = "startAt",
    DURATION = "duration",
    ASSIGNED_TO = "assignedTo",
    REFERENCES = "references",
    APPOINTMENT_LOCATION = "locationName",
    STATUS = "status",
    END_AT_DATE = "endAt",
    CONTACT_ORGANIZATION_NAME = "contactOrganizationName",
    CONTACT_TYPE = "contactType"
}

const columnDefinitions: ColumnDefinition[] = [
    {
        id: AppointmentsColumnIds.IDENTIFIER,
        sortId: "identifierInteger",
        defaultSort: true,
        dataType: DataType.LINK,
        dataAttributes: {
            redirect: "/calendar-events/{id}/edit"
        },
        totalAttributes: {
            key: "totalItemsCount",
            type: TotalType.STRING,
        },
        sortable: true,
        identifier: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: AppointmentsColumnIds.CONTACT,
        accessorFn: row => row.contact?.name,
        sortId: "contact.nameSort",
        dataType: DataType.CONTACT,
        sortable: false,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.CONTACT_RELATED],

    },
    {
        id: AppointmentsColumnIds.TITLE,
        sortId: "titleKeyword",
        dataType: DataType.STRING,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: AppointmentsColumnIds.START_AT_DATE,
        dataType: DataType.DATE,
        sortId: "startAt",
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: AppointmentsColumnIds.DURATION,
        dataType: DataType.COMPONENT,
        sortId:"duration",
        dataAttributes: {
            component: Duration,
            props: {
                duration : AppointmentsColumnIds.DURATION
            }
        },
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: AppointmentsColumnIds.ASSIGNED_TO,
        accessorFn: row => row.user?.name,
        dataType: DataType.STRING,
        sortId:"user.nameSort",
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: AppointmentsColumnIds.REFERENCES,
        dataType: DataType.COMPONENT,
        dataAttributes: {
            component: DisplayReferences,
            props: {
                calendarEvent : "original"
            }
        },
        sortable: false,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT],
    },
    {
        id: AppointmentsColumnIds.APPOINTMENT_LOCATION,
        sortId: "locationNameSort",
        dataType: DataType.STRING,
        sortable: true,
        defaultView: false,
        categories: [ColumnDefinitionCategory.OTHER],
    },
    {
        id: AppointmentsColumnIds.STATUS,
        sortId: "status",
        dataType: DataType.STRING,
        dataAttributes: {
            messageMap: {
                "confirmed": {
                    id: "generic-listing.status-confirmed",
                    defaultMessage: "Confirmed",
                },
                "cancelled": {
                    id: "generic-listing.status-cancelled",
                    defaultMessage: "Cancelled",
                },
            }
        },
        sortable: true,
        defaultView: false,
        categories: [ColumnDefinitionCategory.OTHER],
    },
    {
        id: AppointmentsColumnIds.END_AT_DATE,
        dataType: DataType.DATE,
        sortId: "endAt",
        sortable: true,
        defaultView: false,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: AppointmentsColumnIds.CONTACT_ORGANIZATION_NAME,
        accessorFn: row => row.contact?.organizationName,
        sortId: "contact.organizationName",
        dataType: DataType.STRING,
        sortable: false,
        defaultView: false,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],

    },
    {
        id: AppointmentsColumnIds.CONTACT_TYPE,
        accessorFn: row => row.contact?.type,
        dataType: DataType.STRING,
        dataAttributes: {
            messageMap: {
                "persons": {
                    id: "generic-listing.contact-type.people",
                    defaultMessage: "People",
                },
                "organizations": {
                    id: "generic-listing.contact-type.organization",
                    defaultMessage: "Organization",
                },
                "suppliers": {
                    id: "generic-listing.contact-type.supplier",
                    defaultMessage: "Supplier",
                },
            }
        },
        sortable: false,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
]

const headerMessages = defineMessages({
    [AppointmentsColumnIds.IDENTIFIER]: {
        id: "generic-listing.appointments.table-header.identifier",
        defaultMessage: "ID",
    },
    [AppointmentsColumnIds.CONTACT]: {
        id: "generic-listing.appointments.table-header.contact",
        defaultMessage: "Contact",
    },
    [AppointmentsColumnIds.TITLE]: {
        id: "generic-listing.appointments.table-header.title",
        defaultMessage: "Title",
    },
    [AppointmentsColumnIds.START_AT_DATE]: {
        id: "generic-listing.appointments.table-header.start_at",
        defaultMessage: "Start at",
    },
    [AppointmentsColumnIds.DURATION]: {
        id: "generic-listing.appointments.table-header.duration",
        defaultMessage: "Duration",
    },
    [AppointmentsColumnIds.ASSIGNED_TO]: {
        id: "generic-listing.appointments.table-header.assigned_to",
        defaultMessage: "Assigned to",
    },
    [AppointmentsColumnIds.REFERENCES]: {
        id: "generic-listing.appointments.table-header.references",
        defaultMessage: "Reference",
    },
    [AppointmentsColumnIds.APPOINTMENT_LOCATION]: {
        id: "generic-listing.appointments.table-header.appointment_location",
        defaultMessage: "Appointment location",
    },
    [AppointmentsColumnIds.STATUS]: {
        id: "generic-listing.appointments.table-header.status",
        defaultMessage: "Appointment Status",
    },
    [AppointmentsColumnIds.END_AT_DATE]: {
        id: "generic-listing.appointments.table-header.end_at",
        defaultMessage: "End at",
    },
    [AppointmentsColumnIds.CONTACT_ORGANIZATION_NAME]: {
        id: "generic-listing.appointments.table-header.contact_organization_name",
        defaultMessage: "Contact organization",
    },
    [AppointmentsColumnIds.CONTACT_TYPE]: {
        id: "generic-listing.appointments.table-header.contact_type",
        defaultMessage: "Contact type",
    },

})

const configuration: ObjectTypeConfig = {
    objectType: ObjectType.CALENDAR_EVENTS,
    columnDefinitions,
    headerMessages,
    listingPreferencesKey: PREFERENCE_TYPE.LISTING_CALENDAR_EVENTS,
    searchPlaceholder: {
        id: "generic-listing.appointments.search-placeholder",
        defaultMessage: "Search for an appointment...",
    },
    displayString: {
        id: "generic-listing.appointments.display-string",
        defaultMessage: "Appointments",
    },
    searchObjectType: ObjectType.CALENDAR_EVENTS,
    bulkActionDefinitions: [],
    creationButtonComponent: require("../../../../common/listing/generic-listing/creation-buttons/CalendarEventCreationButton"),
}

export default configuration



// WEBPACK FOOTER //
// ./src/state/listing/generic-listing/column-definitions/calendar-events.ts